import { Alert, Button, Flex, Spin, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { AgentClient } from '../agentClient';
import { staleNote } from '../skillEditView';
import type { SkillEdits, UpdateSkillEdits } from '../skillEdits';
import { useAgentsTab } from '../useAgentsTab';
import AgentCard from './AgentCard';

const AgentsPanel = ({
  client,
  edits,
  onEdits,
}: {
  client: AgentClient | null;
  /** Unsaved skill-editor text, held by the chat panel so it outlives this view. */
  edits: SkillEdits;
  onEdits: UpdateSkillEdits;
}) => {
  const { state, remove, refresh, configured } = useAgentsTab(client);
  const stale = state.status === 'ready' ? staleNote(state.catalog) : null;

  if (!client || !configured) {
    return (
      <Alert
        style={{ margin: 16 }}
        type="info"
        showIcon
        message="The agent service isn't configured, so there is nothing to show here yet."
      />
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
        <Typography.Text type="secondary">
          The agents behind the assistant: what each uses, and what it remembers
          about you.
          {state.catalog?.gitSha ? ` Build ${state.catalog.gitSha}.` : ''}
        </Typography.Text>
        <Button size="small" icon={<ReloadOutlined />} onClick={refresh}>
          Refresh
        </Button>
      </Flex>
      {state.status === 'loading' && (
        <Spin style={{ display: 'block', margin: 24 }} />
      )}
      {state.status === 'error' && (
        <Alert type="error" showIcon message={state.error} />
      )}
      {stale && (
        <Alert
          style={{ marginBottom: 12 }}
          type="warning"
          showIcon
          message={stale}
        />
      )}
      {state.status === 'ready' &&
        state.catalog?.agents.map((agent) => (
          <AgentCard
            key={agent.name}
            agent={agent}
            client={client}
            memory={state.memory[agent.name]}
            onRemove={(a, item) => void remove(a, item)}
            onRetry={refresh}
            edits={edits}
            onEdits={onEdits}
          />
        ))}
    </div>
  );
};

export default AgentsPanel;
